/**
 * Импорт проекта из файла выгрузки.
 *
 * Три состояния в одном окне: выбор файла и режима, вопрос о занятом названии
 * и отчёт. Разделять их нельзя — это один разговор, и человек должен видеть,
 * чем закончилось начатое им действие.
 *
 * Режим выбирается всегда, а не прячется за «дополнительно». Разница между
 * «сделай копию», «подтяни изменения» и «добавь недостающее» — это разница в
 * том, что произойдёт с существующим проектом, и узнавать о ней после нажатия
 * поздно. По той же причине под каждым режимом написано, что он делает, а
 * умолчание — самое безопасное: новый проект.
 *
 * Отчёт показывается всегда, даже когда всё прошло гладко. Перенос почти
 * никогда не бывает полным: часть людей может отсутствовать в системе,
 * содержимого вложений в файле нет. Сказать «готово» и умолчать об этом —
 * значит отдать проект, который выглядит целым и таким не является.
 */

import {
  AlertTriangleIcon,
  ArrowRightIcon,
  CheckCircle2Icon,
  FileJsonIcon,
  Loader2Icon,
  UploadIcon,
} from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import type { ImportCountsDto, ImportMode, ProjectImportResultDto } from '@project-os/contracts';
import { ApiError } from '@/api/client';
import { useImportProject, useProjects } from '@/api/projects';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { pluralRu } from '@/lib/format';

/** Пространства, куда человек вправе импортировать. Сервер проверит повторно. */
export type ImportTarget = { id: string; name: string; role: string };

/**
 * Проект, в который импортируют, когда окно открыто изнутри него.
 *
 * Тогда выбирать нечего: человек уже стоит в проекте, и спрашивать «в какой
 * проект» — значит переспрашивать то, что он только что сказал, открыв окно
 * именно здесь.
 */
export type FixedProject = { id: string; name: string; workspaceId: string };

/**
 * Что делает каждый режим — словами, а не названием.
 *
 * Название режима отвечает на вопрос «как это называется», а человеку перед
 * нажатием нужен ответ на «что случится с моим проектом».
 */
const MODES: { value: ImportMode; label: string; hint: string }[] = [
  {
    value: 'CREATE_NEW',
    label: 'Создать новый проект',
    hint: 'Существующие проекты не затрагиваются.',
  },
  {
    value: 'UPDATE_EXISTING',
    label: 'Обновить существующий',
    hint: 'Где файл новее — берётся файл. Ничего не удаляется.',
  },
  {
    value: 'MERGE',
    label: 'Дополнить существующий',
    hint: 'Добавляется только то, чего в проекте нет. Ничего не переписывается.',
  },
];

/** Режимы, которым нужен проект-получатель. */
function needsTarget(mode: ImportMode): boolean {
  return mode !== 'CREATE_NEW';
}

export function ImportProjectDialog({
  open,
  onOpenChange,
  workspaces,
  project,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaces: ImportTarget[];
  /** Открыто изнутри проекта: получатель известен и не спрашивается. */
  project?: FixedProject;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [workspaceId, setWorkspaceId] = useState(project?.workspaceId ?? workspaces[0]?.id ?? '');
  // Изнутри проекта умолчание другое: сюда приходят применить правку, а не
  // завести копию рядом.
  const [mode, setMode] = useState<ImportMode>(project ? 'UPDATE_EXISTING' : 'CREATE_NEW');
  const [targetProjectId, setTargetProjectId] = useState(project?.id ?? '');
  const [name, setName] = useState('');
  const [conflict, setConflict] = useState<{ message: string; suggested: string } | null>(null);
  const [problem, setProblem] = useState<{ message: string; detail?: string } | null>(null);
  const [result, setResult] = useState<ProjectImportResultDto | null>(null);

  const input = useRef<HTMLInputElement>(null);
  const importProject = useImportProject();

  // Изнутри проекта «создать новый» не предлагается: копию заводят из списка
  // проектов, а здесь речь про этот проект. Названия там же короче: проект
  // уже назван в описании окна.
  const modes = project
    ? [
        {
          value: 'UPDATE_EXISTING' as const,
          label: 'Применить изменения из файла',
          hint: 'Где файл разошёлся с проектом — берётся файл. Ничего не удаляется.',
        },
        {
          value: 'MERGE' as const,
          label: 'Только добавить недостающее',
          hint: 'Ничего не переписывается: появится лишь то, чего в проекте нет.',
        },
      ]
    : MODES;

  // Список нужен только тогда, когда есть куда импортировать: в режиме
  // создания он не спрашивается вовсе.
  const projects = useProjects();
  const candidates = useMemo(
    () =>
      (projects.data ?? []).filter(
        (project) =>
          project.workspaceId === workspaceId &&
          !project.archivedAt &&
          (project.role === 'OWNER' || project.role === 'ADMIN'),
      ),
    [projects.data, workspaceId],
  );

  const reset = () => {
    setFile(null);
    setMode(project ? 'UPDATE_EXISTING' : 'CREATE_NEW');
    setTargetProjectId(project?.id ?? '');
    setName('');
    setConflict(null);
    setProblem(null);
    setResult(null);
  };

  const close = () => {
    reset();
    onOpenChange(false);
  };

  const submit = async (overrideName?: string) => {
    if (!file || !workspaceId) return;
    setProblem(null);

    try {
      const outcome = await importProject.mutateAsync({
        file,
        workspaceId,
        mode,
        name: mode === 'CREATE_NEW' ? (overrideName ?? (name.trim() || undefined)) : undefined,
        targetProjectId: needsTarget(mode) ? targetProjectId || undefined : undefined,
      });
      setConflict(null);
      setResult(outcome);
    } catch (error) {
      if (!(error instanceof ApiError)) {
        setProblem({ message: 'Не удалось импортировать проект' });
        return;
      }
      // Занятое название — не отказ, а вопрос: сервер присылает свободное.
      const suggested = error.fields?.suggestedName;
      if (error.status === 409 && suggested) {
        setConflict({ message: error.message, suggested });
        setName(suggested);
        return;
      }
      setProblem({
        message: error.message,
        detail: error.fields?.file ?? error.fields?.targetProjectId,
      });
    }
  };

  // Проект обязателен там, где импорт пишет в существующий: сервер умеет
  // найти его по файлу, но молча угадывать получателя правки не стоит.
  const ready =
    Boolean(file) && Boolean(workspaceId) && (!needsTarget(mode) || Boolean(targetProjectId));

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) return onOpenChange(true);
        close();
      }}
    >
      <DialogContent className="sm:max-w-lg">
        {result ? (
          <ImportReport result={result} onDone={close} />
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>
                {project ? 'Загрузить изменённый файл' : 'Импортировать проект'}
              </DialogTitle>
              <DialogDescription>
                {project
                  ? `Файл, скачанный кнопкой «Скачать проект» и поправленный снаружи. Изменения лягут в «${project.name}».`
                  : 'Файл, скачанный кнопкой «Скачать проект». Ни один режим ничего не удаляет.'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="import-file">Файл выгрузки</Label>
                <input
                  ref={input}
                  id="import-file"
                  type="file"
                  accept="application/json,.json"
                  className="sr-only"
                  onChange={(event) => {
                    setFile(event.target.files?.[0] ?? null);
                    setConflict(null);
                    setProblem(null);
                  }}
                />
                <Button
                  variant="outline"
                  className="w-full justify-start font-normal"
                  onClick={() => input.current?.click()}
                >
                  <FileJsonIcon />
                  <span className="min-w-0 flex-1 truncate text-left">
                    {file ? file.name : 'Выберите файл…'}
                  </span>
                  {file ? (
                    <span className="shrink-0 text-2xs text-muted-foreground tabular-nums">
                      {Math.max(1, Math.round(file.size / 1024))} КБ
                    </span>
                  ) : null}
                </Button>
              </div>

              <fieldset className="space-y-2">
                <legend className="mb-2 text-sm leading-none font-medium">Что сделать</legend>
                <div className="space-y-1.5">
                  {modes.map((entry) => (
                    <label
                      key={entry.value}
                      className={cn(
                        'row-hover flex cursor-pointer items-start gap-2.5 rounded-md border p-2.5',
                        mode === entry.value ? 'border-primary bg-accent/40' : 'border-border',
                      )}
                    >
                      <input
                        type="radio"
                        name="import-mode"
                        value={entry.value}
                        checked={mode === entry.value}
                        onChange={() => {
                          setMode(entry.value);
                          setConflict(null);
                          setProblem(null);
                        }}
                        className="mt-0.5 accent-primary"
                      />
                      <span className="min-w-0">
                        <span className="block text-xs font-medium">{entry.label}</span>
                        <span className="block text-2xs leading-relaxed text-muted-foreground">
                          {entry.hint}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>

              {!project && workspaces.length > 1 ? (
                <div className="space-y-2">
                  <Label htmlFor="import-workspace">Рабочее пространство</Label>
                  <Select
                    value={workspaceId}
                    onValueChange={(next) => {
                      setWorkspaceId(next);
                      // Проект из другого пространства получателем быть не может.
                      setTargetProjectId('');
                    }}
                  >
                    <SelectTrigger id="import-workspace" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {workspaces.map((workspace) => (
                        <SelectItem key={workspace.id} value={workspace.id}>
                          {workspace.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : null}

              {!project && needsTarget(mode) ? (
                <div className="space-y-2">
                  <Label htmlFor="import-target">Какой проект</Label>
                  {candidates.length === 0 ? (
                    <p className="rounded-md border border-tone-warning-border bg-tone-warning-bg p-3 text-2xs leading-relaxed">
                      В этом пространстве нет проектов, куда вы вправе импортировать. Обновлять и
                      дополнять может владелец или администратор проекта.
                    </p>
                  ) : (
                    <>
                      <Select value={targetProjectId} onValueChange={setTargetProjectId}>
                        <SelectTrigger id="import-target" className="w-full">
                          <SelectValue placeholder="Выберите проект…" />
                        </SelectTrigger>
                        <SelectContent>
                          {candidates.map((project) => (
                            <SelectItem key={project.id} value={project.id}>
                              {project.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-2xs leading-relaxed text-muted-foreground">
                        {mode === 'UPDATE_EXISTING'
                          ? 'Совпадения ищутся по идентификаторам из файла, а затем по названиям. Спор решается в пользу более поздней даты изменения.'
                          : 'Совпадения пропускаются, недостающее добавляется. Уже существующее остаётся как есть.'}
                      </p>
                    </>
                  )}
                </div>
              ) : null}

              {conflict ? (
                <div className="space-y-2 rounded-md border border-tone-warning-border bg-tone-warning-bg p-3">
                  <p className="flex items-start gap-2 text-xs leading-relaxed">
                    <AlertTriangleIcon className="mt-px size-4 shrink-0 text-tone-warning" />
                    <span>
                      {conflict.message} Название можно изменить — предложено свободное. Если это
                      тот же проект, выберите «Обновить существующий».
                    </span>
                  </p>
                  <Input
                    aria-label="Название нового проекта"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        void submit(name.trim() || conflict.suggested);
                      }
                    }}
                  />
                </div>
              ) : null}

              {problem ? (
                <div className="rounded-md border border-tone-danger-border bg-tone-danger-bg p-3">
                  <p className="text-xs font-medium">{problem.message}</p>
                  {problem.detail ? (
                    <p className="mt-1 text-2xs leading-relaxed text-muted-foreground">
                      {problem.detail}
                    </p>
                  ) : null}
                </div>
              ) : null}

              {project ? (
                <p className="text-2xs leading-relaxed text-muted-foreground/80">
                  Правка в файле применяется, даже если даты внутри не менялись: точкой отсчёта
                  служит момент выгрузки. Записи, изменённые в проекте уже после неё, остаются как
                  есть — о них скажут отдельно. Удаление записи из файла не удаляет её из проекта.
                </p>
              ) : null}

              <p className="text-2xs leading-relaxed text-muted-foreground/80">
                Участники восстанавливаются, если человек с таким адресом уже есть в системе —
                учётные записи из файла не создаются. Вложения переносятся только тогда, когда сам
                файл нашёлся в хранилище этой установки: в выгрузке лежит перечень, а не содержимое.
              </p>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={close}>
                Отмена
              </Button>
              <Button
                disabled={!ready || importProject.isPending}
                onClick={() =>
                  void submit(conflict ? name.trim() || conflict.suggested : undefined)
                }
              >
                {importProject.isPending ? (
                  <Loader2Icon className="animate-spin" />
                ) : (
                  <UploadIcon />
                )}
                {conflict
                  ? 'Импортировать под этим именем'
                  : project
                    ? 'Применить к проекту'
                    : 'Импортировать'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Подписи счётчиков: один порядок на все три колонки. */
const COUNTS: [keyof ImportCountsDto, string][] = [
  ['items', 'элементы'],
  ['stages', 'этапы'],
  ['requirements', 'требования'],
  ['relations', 'связи'],
  ['comments', 'комментарии'],
  ['workstreams', 'направления'],
  ['members', 'участники'],
  ['attachments', 'вложения'],
];

/**
 * Что получилось — и чего не получилось.
 *
 * Три колонки, а не одна цифра: при слиянии «создано ноль» означает, что файл
 * не принёс ничего нового, и это успех, а не пустой результат. Строка
 * показывается, если хоть в одной колонке есть число: иначе таблица заросла бы
 * нулями.
 */
function ImportReport({ result, onDone }: { result: ProjectImportResultDto; onDone: () => void }) {
  const rows = COUNTS.map(([field, label]) => ({
    label,
    created: result.restored[field],
    updated: result.updated[field],
    skipped: result.skipped[field],
  })).filter((row) => row.created + row.updated + row.skipped > 0);

  const headline =
    result.mode === 'CREATE_NEW'
      ? 'Проект импортирован'
      : result.mode === 'UPDATE_EXISTING'
        ? 'Проект обновлён'
        : 'Проект дополнен';

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <CheckCircle2Icon className="size-4 text-tone-success" />
          {headline}
        </DialogTitle>
        <DialogDescription>
          «{result.name}»{result.renamed ? ' — название изменено, прежнее было занято.' : ''}
          {result.mode === 'UPDATE_EXISTING' && !result.projectUpdated
            ? ' Сведения о самом проекте оставлены прежними.'
            : ''}
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-3">
        {rows.length > 0 ? (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-2xs text-muted-foreground">
                <th className="pb-1 text-left font-normal">&nbsp;</th>
                <th className="pb-1 text-right font-normal">создано</th>
                <th className="pb-1 text-right font-normal">обновлено</th>
                <th className="pb-1 text-right font-normal">пропущено</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.label}>
                  <td className="py-0.5 text-muted-foreground">{row.label}</td>
                  <td className="py-0.5 text-right font-medium tabular-nums">
                    {row.created || '—'}
                  </td>
                  <td className="py-0.5 text-right font-medium tabular-nums">
                    {row.updated || '—'}
                  </td>
                  <td className="py-0.5 text-right font-medium tabular-nums">
                    {row.skipped || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-xs text-muted-foreground">
            Файл не принёс ничего, чего в проекте ещё нет.
          </p>
        )}

        {result.warnings.length > 0 ? (
          <div className="space-y-1.5 rounded-md border border-tone-warning-border bg-tone-warning-bg p-3">
            <p className="flex items-center gap-1.5 text-xs font-medium">
              <AlertTriangleIcon className="size-3.5 text-tone-warning" />
              {pluralRu(result.warnings.length, ['Замечание', 'Замечания', 'Замечаний'])} к переносу
            </p>
            <ul className="space-y-1">
              {result.warnings.map((warning) => (
                <li key={warning} className="text-2xs leading-relaxed text-muted-foreground">
                  {warning}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onDone}>
          Закрыть
        </Button>
        <Button asChild className="icon-nudge">
          <Link to={`/projects/${result.projectId}`} onClick={onDone}>
            Открыть проект
            <ArrowRightIcon />
          </Link>
        </Button>
      </DialogFooter>
    </>
  );
}
